import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  defaultAddDefaults,
  defaultOperatorAddDefaults,
  hasKey as listHasKey,
  addItem as listAddItem,
  removeItem as listRemoveItem,
  applyJsonToItems,
} from '../utils/syncedListHelpers';
import {
  User, Mail, Lock, Bell, CreditCard, Wrench,
  Calendar, Plus, X, CheckCircle, Shield, Zap,
  AlertTriangle, Clock, Download, ChevronRight, RefreshCw, Link2
} from 'lucide-react';

const TOOL_LABELS = {
  '/ads-txt':      { label: 'Ads.txt / App-ads.txt Checker', tier: 'Free' },
  '/sellers-json': { label: 'Sellers.json Checker', tier: 'Pro' },
  '/crawler':      { label: 'Ads.txt Crawler', tier: 'Pro' },
  '/bundles':      { label: 'Bundle Tracker', tier: 'Pro' },
  '/bundle-lookup':{ label: 'Bundle Lookup', tier: 'Pro' },
  '/schain':       { label: 'Schain Validator', tier: 'Pro' },
  '/bid-request':  { label: 'Bid Request Analyzer', tier: 'Pro' },
  '/discrepancy':  { label: 'Discrepancy Checker', tier: 'Pro' },
  '/app-category': { label: 'App Category Segmenter', tier: 'Pro' },
  '/disc-monitor': { label: 'Discrepancy Monitor', tier: 'Pro' },
};

function Section({ icon: Icon, title, children, color = 'var(--cyan)' }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ width: 32, height: 32, borderRadius: '8px', background: `rgba(0,0,0,0.2)`, border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} style={{ color }} />
        </div>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ── Payment Calendar ───────────────────────────────────────────────────────────
function PaymentCalendar({ renewsAt }) {
  if (!renewsAt) return (
    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px' }}>
      No active subscription renewal date found.
    </div>
  );

  const now = new Date();
  const due = new Date(renewsAt);
  const daysLeft = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  const isOverdue = daysLeft < 0;
  const isUrgent = daysLeft >= 0 && daysLeft <= 7;
  const isWarning = daysLeft > 7 && daysLeft <= 30;

  const color = isOverdue ? 'var(--red)' : isUrgent ? 'var(--amber)' : isWarning ? 'var(--amber)' : 'var(--green)';
  // Text on the due-day fill. Derived from the SAME condition as `color` so the
  // two cannot drift — the old code missed isWarning and put black on amber.
  const onColor = isOverdue ? 'var(--on-red)' : (isUrgent || isWarning) ? 'var(--on-amber)' : 'var(--on-green)';
  const bg = isOverdue ? 'color-mix(in srgb, var(--red) 8%, transparent)' : isUrgent ? 'color-mix(in srgb, var(--amber) 8%, transparent)' : 'color-mix(in srgb, var(--green) 6%, transparent)';
  const border = isOverdue ? 'color-mix(in srgb, var(--red) 30%, transparent)' : isUrgent ? 'color-mix(in srgb, var(--amber) 25%, transparent)' : 'color-mix(in srgb, var(--green) 20%, transparent)';

  // Build mini calendar
  const month = due.getMonth();
  const year = due.getFullYear();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = due.toLocaleString('default', { month: 'long', year: 'numeric' });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      {/* Due date banner */}
      <div style={{ padding: '14px 18px', background: bg, border: `1px solid ${border}`, borderRadius: 'var(--radius)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Calendar size={20} style={{ color, flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
            {isOverdue ? 'Payment Overdue' : `Payment due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Next billing date: <span style={{ color, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
              {due.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: '36px', fontWeight: 900, color, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
            {isOverdue ? '!' : daysLeft}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {isOverdue ? 'overdue' : 'days left'}
          </div>
        </div>
      </div>

      {/* Mini calendar */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '14px', border: '1px solid var(--border)' }}>
        <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '12px' }}>{monthName}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px', textAlign: 'center' }}>
          {['S','M','T','W','T','F','S'].map((d,i) => (
            <div key={i} style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700, padding: '4px 0' }}>{d}</div>
          ))}
          {cells.map((d, i) => {
            const isDueDay = d === due.getDate();
            const isToday = d && new Date(year, month, d).toDateString() === now.toDateString();
            return (
              <div key={i} style={{
                fontSize: '11px', padding: '5px 2px', borderRadius: '4px',
                background: isDueDay ? color : isToday ? 'var(--bg-hover)' : 'transparent',
                color: isDueDay ? onColor : isToday ? 'var(--cyan)' : d ? 'var(--text-secondary)' : 'transparent',
                fontWeight: isDueDay || isToday ? 800 : 400,
                border: isDueDay ? 'none' : isToday ? '1px solid var(--cyan)' : '1px solid transparent',
              }}>
                {d || ''}
              </div>
            );
          })}
        </div>
        {(isUrgent || isOverdue) && (
          <div style={{ marginTop: '12px', padding: '8px', background: 'color-mix(in srgb, var(--amber) 8%, transparent)', borderRadius: 'var(--radius-sm)', fontSize: '11px', color: 'var(--amber)', textAlign: 'center' }}>
            {isOverdue ? '⚠️ Payment overdue — please contact support@ignitorads.com' : `⚠️ Payment due soon — contact us at support@ignitorads.com`}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Partner API Configs Section ──────────────────────────────────────────────
const AUTH_TYPES  = [
  { value: 'bearer',     label: 'Bearer Token' },
  { value: 'api_key',    label: 'API Key (X-API-Key header)' },
  { value: 'basic',      label: 'Basic Auth (user:pass)' },
  { value: 'topon_md5',  label: 'Signed Request (TopOn MD5)' },
  { value: 'none',       label: 'No Authentication' },
];
// Second credential — for partners that need TWO headers (e.g. X-API-Key AND
// Authorization: Bearer). topon_md5 is excluded: it signs the whole request
// rather than adding a static header, so it can only ever be the primary.
const AUTH_TYPES_2 = [
  { value: 'none',       label: 'None (single credential)' },
  { value: 'api_key',    label: 'API Key (X-API-Key header)' },
  { value: 'bearer',     label: 'Bearer Token' },
  { value: 'basic',      label: 'Basic Auth (user:pass)' },
];
// 'bearer' and 'basic' BOTH write Authorization — pairing them would mean one
// silently overwrites the other. Mirrors AUTH_HEADER on the server.
const AUTH_HEADER_OF = { bearer: 'Authorization', basic: 'Authorization', api_key: 'X-API-Key' };
// Per-auth-type field metadata for the credential input — drives the label,
// placeholder, and inline hint so users see what each auth flow expects.
const AUTH_FIELD_META = {
  bearer:    { label: 'Bearer Token',      placeholder: 'Paste your bearer token', hint: '' },
  api_key:   { label: 'API Key',           placeholder: 'Paste your API key',      hint: 'Sent as X-API-Key header' },
  basic:     { label: 'Username:Password', placeholder: 'user:pass',                hint: 'Colon-separated. We base64-encode for you.' },
  topon_md5: { label: 'Publisher Key',     placeholder: 'Paste your TopOn publisher key', hint: 'Sent as X-Up-Key. We compute X-Up-Timestamp + X-Up-Signature per request.' },
};
const ROLES = [
  { value: 'demand',   label: 'Demand Partner (DSP / Advertiser)' },
  { value: 'supply',   label: 'Supply Partner (SSP / Exchange)' },
  { value: 'adserver', label: 'Ad Server' },
];
const METHODS = ['POST', 'GET', 'PUT'];

const EMPTY_CONFIG = {
  name: '', role: 'demand', endpoint_url: '', method: 'POST',
  auth_type: 'bearer', auth_token: '',
  auth_type_2: 'none',  auth_token_2: '',
  // Sprint 3.5 minor changes: prefill Content-Type for new configs since most
  // ad-tech reporting APIs accept/return JSON. Existing configs are loaded from
  // the API response and bypass this default — no risk of overwriting partner-
  // specific headers like Bidmachine's "Accept: application/x-ndjson".
  headers: '{\n  "Content-Type": "application/json"\n}',
  request_body: '{}',
  response_mapping: '{"date":"date","impressions":"impressions","revenue":"revenue","wins":"","bundle":"","advertiser":"","rows_path":""}',
  date_format: 'YYYY-MM-DD', notes: '', docs_url: '',
  // Sprint 3 fields. Default filter_shape to null so the picker forces an
  // explicit choice on new partners. dimensions/metrics default to empty
  // arrays; the user populates them via the editor (auto-detect or manually).
  filter_shape: null, dimensions: [], metrics: [],
  // Optional: the API request dimension that returns the app bundle ID, used by
  // the Auto Monitor "Fetch bundle-level discrepancy" feature. Empty = partner
  // doesn't report bundle-level data.
  bundle_dimension: '',
  // Sprint 3.5: operator vocabulary — each entry shaped { display, wire }.
  // Default-empty; users pick from OPERATOR_PRESETS or add freeform.
  operator_vocabulary: [],
};

// Common AdTech filter operator wire values, shown as preset chips in the
// operator editor. Users click to add; freeform input handles partner-specific
// wire values not in this list. Storage shape is { display, wire } per entry
// (with display === wire by default).
const OPERATOR_PRESETS = [
  '=', '!=', '>', '<', '>=', '<=',
  'eq', 'ne', 'gt', 'lt', 'gte', 'lte',
  'in', 'not_in', 'contains', 'not_contains', 'like',
  'between', 'is_null', 'is_not_null',
];

const FILTER_SHAPES = [
  { value: 'json-values-array',
    label: 'JSON: values array',
    desc: 'Filters are objects like {"key":"country","values":["US","UK"],"operator":"in"}.' },
  { value: 'json-name-value-match',
    label: 'JSON: name / value / match',
    desc: 'Filters are objects like {"name":"country","value":["US"],"match":"eq"}.' },
  { value: 'json-field-value-operator',
    label: 'JSON: field / value / operator',
    desc: 'Filters are objects like {"field":"country","value":["US"],"operator":"="}.' },
  { value: 'query-string-only',
    label: 'No filtering support',
    desc: 'API supports no field filtering — only date range and pagination.' },
];

function JsonField({ label, value, onChange, hint }) {
  const [err, setErr] = React.useState('');
  function handleChange(v) {
    onChange(v);
    try { JSON.parse(v); setErr(''); }
    catch (e) { setErr('Invalid JSON'); }
  }
  return (
    <div>
      <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>{label}</label>
      <textarea className="input-field" style={{ width: '100%', minHeight: '90px', fontFamily: 'var(--font-mono)', fontSize: '11px', resize: 'vertical', boxSizing: 'border-box', borderColor: err ? 'var(--red)' : undefined }}
        value={value} onChange={e => handleChange(e.target.value)} />
      {err && <div style={{ fontSize: '10px', color: 'var(--red)', marginTop: '2px' }}>✗ {err}</div>}
      {hint && !err && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

// ─── SyncedListEditor — Sprint 3 ──────────────────────────────────────────────
// Three-way synced editor for dimension/metric arrays. Source of truth is the
// parent's `value` prop (internal-state-as-truth, NOT JSON-as-truth — JSON view
// is a serialized render of state, editable via explicit Apply button).
//
// THREE VIEWS, ALL READING FROM `value`:
//   1. Dropdown — multi-select of detected suggestions (greyed if already added)
//   2. Freeform — text input + Add button for items not in the dropdown
//   3. JSON textarea — read-only render of value until user starts editing.
//      In edit mode, draft is held locally; Apply parses + replaces value;
//      Fix Syntax uses jsonrepair to auto-fix common issues; Cancel discards.
//
// CRITICAL: Dropdown is empty until parent runs auto-detect ("Suggest from docs"
// button). Suggestions live in `suggestions` prop. Empty state shows guidance.
//
// Used for: dimensions, metrics. Could extend to operators in Sprint 3 followup.
function SyncedListEditor({
  label, value, onChange,
  suggestions, isDetecting, onSuggest,
  helpText, addDefaults,
  // Sprint 3.5: keyField names which property identifies items. Default 'key'
  // (dimensions/metrics). Operators pass 'wire'.
  keyField = 'key',
  // Optional placeholder shown in the freeform input (sensible default).
  freeformPlaceholder = 'e.g. country, publisher_id',
  // When true, the "🪄 Suggest from docs" button is hidden — operators don't
  // need doc-based detection (we use a curated preset dropdown instead).
  hideSuggestButton = false,
  // Optional preset list of values shown as clickable chips (operators use this
  // in place of detected suggestions). Each entry is a plain string value.
  presetValues = null,
  // Optional pre-section block: e.g. helper note when filter_shape suggests no
  // filters. Rendered above the main editor.
  preNote = null,
}) {
  const items = Array.isArray(value) ? value : [];
  const safeAddDefaults = addDefaults || defaultAddDefaults;
  const [freeformKey, setFreeformKey] = React.useState('');
  const [jsonDraft, setJsonDraft]     = React.useState(null);   // null = not editing
  const [jsonErr, setJsonErr]         = React.useState('');

  function hasKey(k) { return listHasKey(items, k, keyField); }
  function addItem(newItemOrKey) {
    const next = listAddItem(items, newItemOrKey, safeAddDefaults, keyField);
    if (next !== items) onChange(next);
  }
  function removeItem(key) { onChange(listRemoveItem(items, key, keyField)); }

  // Display label for a chip — use label/display if present, else the keyField value.
  function chipLabel(item) {
    if (!item) return '';
    return item.label || item.display || item[keyField] || '';
  }

  function toggleSuggestion(s) {
    const k = s[keyField];
    hasKey(k) ? removeItem(k) : addItem(s);
  }

  function togglePreset(value) {
    // For operators: preset is a string. Convert through factory.
    if (hasKey(value)) removeItem(value);
    else addItem(value);
  }

  function handleAddFreeform() {
    const k = freeformKey.trim();
    if (!k || hasKey(k)) { setFreeformKey(''); return; }
    addItem(k);
    setFreeformKey('');
  }

  // ─── JSON edit mode ────────────────────────────────────────────────────────
  function startJsonEdit() {
    setJsonDraft(JSON.stringify(items, null, 2));
    setJsonErr('');
  }
  function cancelJsonEdit() { setJsonDraft(null); setJsonErr(''); }
  function applyJsonEdit() {
    const result = applyJsonToItems(jsonDraft, keyField);
    if (result.ok) {
      onChange(result.items);
      setJsonDraft(null);
      setJsonErr('');
    } else {
      setJsonErr(result.error);
    }
  }
  function fixJsonSyntax() {
    try {
      // Lazy require so jsonrepair is only loaded when actually used.
      // eslint-disable-next-line global-require
      const { jsonrepair } = require('jsonrepair');
      const repaired = jsonrepair(jsonDraft || '');
      setJsonDraft(repaired);
      // Try parse after repair to surface any remaining issue immediately.
      try { JSON.parse(repaired); setJsonErr(''); }
      catch (e) { setJsonErr('Repair partial: ' + e.message); }
    } catch (e) {
      setJsonErr('Could not auto-fix: ' + e.message);
    }
  }

  const newSuggestions = suggestions?.filter(s => !hasKey(s[keyField])) || [];
  const newPresets = presetValues?.filter(v => !hasKey(v)) || [];

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: '12px', background: 'var(--bg-elev)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{label}
          <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--text-muted)', fontWeight: 400 }}>
            {items.length} added
          </span>
        </label>
        {!hideSuggestButton && (
          <button type="button" className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }}
            onClick={onSuggest} disabled={isDetecting}>
            {isDetecting ? '⏳ Detecting...' : '🪄 Suggest from docs'}
          </button>
        )}
      </div>
      {helpText && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '8px', lineHeight: 1.4 }}>{helpText}</div>}
      {preNote && (
        <div style={{ fontSize: '10px', color: 'var(--amber)', marginBottom: '8px', fontStyle: 'italic', lineHeight: 1.4 }}>
          {preNote}
        </div>
      )}

      {/* ─── View 1a: Preset values (operators) — only if presetValues passed ── */}
      {presetValues && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
            Common operators ({newPresets.length} available)
          </div>
          {newPresets.length === 0 ? (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 0' }}>
              ✓ All preset operators already added.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxHeight: '120px', overflowY: 'auto' }}>
              {newPresets.map(v => (
                <button key={v} type="button"
                  onClick={() => togglePreset(v)}
                  title={v}
                  style={{
                    fontSize: '11px', padding: '3px 8px', borderRadius: '12px',
                    border: '1px solid var(--border)', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-mono)',
                  }}>
                  + {v}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── View 1b: Detected suggestions dropdown — only if not using presets ── */}
      {!presetValues && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
            From docs ({newSuggestions.length} available)
          </div>
          {(!suggestions || suggestions.length === 0) ? (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 0' }}>
              Click "🪄 Suggest from docs" to auto-detect from this partner's API documentation.
            </div>
          ) : newSuggestions.length === 0 ? (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 0' }}>
              ✓ All detected items already added.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxHeight: '120px', overflowY: 'auto' }}>
              {newSuggestions.map(s => (
                <button key={s[keyField]} type="button"
                  onClick={() => toggleSuggestion(s)}
                  title={s.hint || s.label || s.display}
                  style={{
                    fontSize: '11px', padding: '3px 8px', borderRadius: '12px',
                    border: '1px solid var(--border)', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-mono)',
                  }}>
                  + {s[keyField]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── View 2: Freeform add ─────────────────────────────────────────── */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
          Add manually
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input className="input-field" type="text" placeholder={freeformPlaceholder}
            value={freeformKey}
            onChange={e => setFreeformKey(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddFreeform(); } }}
            style={{ flex: 1, fontSize: '11px', padding: '4px 8px', fontFamily: 'var(--font-mono)' }} />
          <button type="button" className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 12px' }}
            onClick={handleAddFreeform} disabled={!freeformKey.trim() || hasKey(freeformKey.trim())}>
            Add
          </button>
        </div>
      </div>

      {/* ─── Current items chips ──────────────────────────────────────────── */}
      {items.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
            Current ({items.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {items.map(it => (
              <span key={it[keyField]} style={{
                fontSize: '11px', padding: '3px 8px', borderRadius: '12px',
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                fontFamily: 'var(--font-mono)', display: 'inline-flex', gap: '6px', alignItems: 'center',
              }}>
                {it[keyField]}
                <button type="button" onClick={() => removeItem(it[keyField])}
                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 0, fontSize: '12px', lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ─── View 3: JSON view (read-only render, editable on demand) ─────── */}
      <details style={{ marginTop: '8px' }}>
        <summary style={{ fontSize: '11px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
          ▶ Advanced: JSON view
        </summary>
        <div style={{ marginTop: '6px' }}>
          {jsonDraft === null ? (
            <>
              <pre style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '4px',
                padding: '8px', fontSize: '10px', fontFamily: 'var(--font-mono)',
                margin: 0, maxHeight: '180px', overflow: 'auto', whiteSpace: 'pre-wrap',
              }}>{JSON.stringify(items, null, 2)}</pre>
              <button type="button" className="btn btn-secondary" style={{ marginTop: '6px', fontSize: '11px', padding: '4px 10px' }}
                onClick={startJsonEdit}>Edit JSON</button>
            </>
          ) : (
            <>
              <textarea
                value={jsonDraft}
                onChange={e => { setJsonDraft(e.target.value); setJsonErr(''); }}
                style={{
                  width: '100%', minHeight: '160px', fontFamily: 'var(--font-mono)',
                  fontSize: '11px', padding: '8px', borderRadius: '4px', boxSizing: 'border-box',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                  border: '1px solid ' + (jsonErr ? 'var(--red)' : 'var(--border)'),
                }} />
              {jsonErr && <div style={{ fontSize: '10px', color: 'var(--red)', marginTop: '2px' }}>✗ {jsonErr}</div>}
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                <button type="button" className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 10px' }}
                  onClick={applyJsonEdit}>Apply</button>
                <button type="button" className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }}
                  onClick={fixJsonSyntax}>🔧 Fix Syntax</button>
                <button type="button" className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }}
                  onClick={cancelJsonEdit}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </details>
    </div>
  );
}

function PartnerConfigModal({ config, onSave, onClose }) {
  const [form, setForm] = React.useState({
    ...EMPTY_CONFIG,
    ...(config ? {
      ...config,
      auth_token: '',    // never pre-fill token
      auth_token_2: '',  // never pre-fill token
      auth_type_2: config.auth_type_2 || 'none',
      headers: JSON.stringify(config.headers || {}, null, 2),
      request_body: JSON.stringify(config.request_body || {}, null, 2),
      response_mapping: JSON.stringify(config.response_mapping || {}, null, 2),
      docs_url: config.docs_url || '',
      // Sprint 3 fields are already arrays/string in the API response; pass through.
      filter_shape: config.filter_shape ?? null,
      dimensions:   Array.isArray(config.dimensions) ? config.dimensions : [],
      metrics:      Array.isArray(config.metrics)    ? config.metrics    : [],
      // Sprint 3.5: operator vocabulary
      operator_vocabulary: Array.isArray(config.operator_vocabulary) ? config.operator_vocabulary : [],
    } : {})
  });
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);

  // ─── Sprint 3: doc-detection state ─────────────────────────────────────────
  // Suggestions live separately from form state because they're presentational
  // (drive the dropdown view of SyncedListEditor) rather than data being saved.
  // They persist across re-renders of the modal but reset when modal opens.
  // Both dimensions and metrics share suggestion lists — one /suggest-fields
  // call populates both at once for efficiency.
  const [detectedDims, setDetectedDims]       = React.useState([]);
  const [detectedMetrics, setDetectedMetrics] = React.useState([]);
  const [isDetecting, setIsDetecting]         = React.useState(false);

  // ─── Sprint 2.5 Commit (b): validate-before-save ───────────────────────────
  // Tracks the signature of form values at last successful test. Save is gated
  // until current form values match this signature. Five fields invalidate:
  // endpoint_url, auth_type, auth_token, headers, request_body. Cosmetic fields
  // like name/notes/docs_url don't gate Save.
  //
  // For NEW configs:      lastTestedSig starts null → must test before save.
  // For EXISTING configs: "trust existing" — initialize lastTestedSig to current
  //                       signature, so opening the modal and saving cosmetic
  //                       edits doesn't require a re-test. Edits to any of the
  //                       5 hashed fields diverge the signatures → Save disables
  //                       until a successful re-test.
  function computeSig(f) {
    return JSON.stringify({
      url:   f.endpoint_url || '',
      auth:  f.auth_type    || 'none',
      // For existing configs the auth_token field is blank on open. We sign on
      // "blank means unchanged" so existing configs stay trusted; once the user
      // types anything in the token field, signature diverges → must retest.
      token: f.auth_token   || '',
      // Slot 2 must be in the signature too, or you could change the second
      // credential and save it without ever re-testing.
      auth2:  f.auth_type_2  || 'none',
      token2: f.auth_token_2 || '',
      headers: f.headers    || '{}',
      body:    f.request_body || '{}',
    });
  }
  // For NEW: null → must test first. For EXISTING: trust the current state.
  const [lastTestedSig, setLastTestedSig] = React.useState(() =>
    config?.id ? computeSig({
      ...EMPTY_CONFIG,
      ...config,
      auth_token: '',
      headers: JSON.stringify(config.headers || {}, null, 2),
      request_body: JSON.stringify(config.request_body || {}, null, 2),
    }) : null
  );

  const currentSig = computeSig(form);
  const isTestValid = lastTestedSig !== null && lastTestedSig === currentSig;
  const saveBlockedReason = !isTestValid
    ? (lastTestedSig === null
        ? 'Run Test Connection successfully before saving.'
        : 'Form has changed since last successful test — re-test before saving.')
    : null;

  function update(k, v) { setForm(f => ({ ...f, [k]: v })); setTestResult(null); }

  function isJsonValid(str) { try { JSON.parse(str); return true; } catch { return false; } }

  async function handleSave() {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.endpoint_url.trim()) return toast.error('Endpoint URL is required');
    if (!isJsonValid(form.headers)) return toast.error('Headers must be valid JSON');
    if (!isJsonValid(form.request_body)) return toast.error('Request Body must be valid JSON');
    if (!isJsonValid(form.response_mapping)) return toast.error('Response Mapping must be valid JSON');
    setSaving(true);
    try {
      const payload = {
        ...form,
        headers: JSON.parse(form.headers),
        request_body: JSON.parse(form.request_body),
        response_mapping: JSON.parse(form.response_mapping),
      };
      if (!payload.auth_token)   delete payload.auth_token;   // blank = keep saved
      if (!payload.auth_token_2) delete payload.auth_token_2; // blank = keep saved
      const { data } = config?.id
        ? await axios.put(`/api/partner-configs/${config.id}`, payload)
        : await axios.post('/api/partner-configs', payload);
      toast.success(config?.id ? 'Config updated' : 'Config saved');
      onSave(data.config);
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  }

  async function handleTest() {
    // Sprint 2.5: tests current form values via /test-draft endpoint — no DB write.
    // Form must have URL at minimum. Other validity checks happen server-side
    // (SSRF prevention, rate limit). Successful test updates lastTestedSig so the
    // Save button can become enabled.
    if (!form.endpoint_url.trim()) return toast.error('Endpoint URL required to test');
    if (!isJsonValid(form.headers))         return toast.error('Headers must be valid JSON');
    if (!isJsonValid(form.request_body))    return toast.error('Request Body must be valid JSON');
    if (!isJsonValid(form.response_mapping)) return toast.error('Response Mapping must be valid JSON');

    setTesting(true); setTestResult(null);
    // Capture the signature BEFORE the network call. If user edits during the
    // ~45s test window, isTestValid stays false on success because lastTestedSig
    // would match an old form state, not what the user sees now.
    const sigAtTestTime = currentSig;
    try {
      const payload = {
        ...form,
        headers:          JSON.parse(form.headers),
        request_body:     JSON.parse(form.request_body),
        response_mapping: JSON.parse(form.response_mapping),
      };
      // For existing configs with blank auth_token field, tell the backend to
      // pull the saved (encrypted) token from this config id and combine it with
      // the user's current form values. This means users don't need to retype
      // their token just to test other edits — but if they DO type a token,
      // that overrides and the saved one isn't fetched.
      if (config?.id && ((config.has_token   && !form.auth_token) ||
                         (config.has_token_2 && !form.auth_token_2))) {
        payload.use_saved_token_from_id = config.id;
      }
      const { data } = await axios.post('/api/partner-configs/test-draft', payload);
      setTestResult(data);
      if (data.success) {
        setLastTestedSig(sigAtTestTime);
        toast.success(data.message);
      } else {
        setLastTestedSig(null);
        toast.error(data.error || 'Test failed');
      }
    } catch (e) {
      const errMsg = e.response?.data?.error || 'Test failed';
      setTestResult({ success: false, error: errMsg });
      setLastTestedSig(null);
      toast.error(errMsg);
    } finally { setTesting(false); }
  }

  // ─── Sprint 3: Auto-detect dimensions/metrics from API docs ────────────────
  // Calls POST /api/partner-configs/suggest-fields, which fetches the docs URL
  // and asks Claude (Haiku 4.5) to extract structured field definitions.
  // Results populate the SyncedListEditor's dropdown suggestions; the user
  // multi-selects which to keep. If filter_shape is detected and not already
  // set in the form, it's auto-applied (low-risk: empty → suggestion).
  async function handleSuggestFields() {
    if (!form.docs_url || !form.docs_url.trim()) {
      return toast.error('Add an API Documentation URL first');
    }
    setIsDetecting(true);
    try {
      const { data } = await axios.post('/api/partner-configs/suggest-fields', {
        docs_url: form.docs_url.trim(),
      });
      if (data.success) {
        setDetectedDims(Array.isArray(data.dimensions) ? data.dimensions : []);
        setDetectedMetrics(Array.isArray(data.metrics) ? data.metrics : []);
        // Auto-apply filter_shape if detected AND not already set
        if (data.filter_shape && !form.filter_shape) {
          update('filter_shape', data.filter_shape);
        }
        toast.success(data.message || 'Suggestions ready');
      } else {
        toast.error(data.error || 'Suggestion failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Suggestion failed — verify the docs URL is publicly accessible');
    } finally { setIsDetecting(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '680px', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>
            {config?.id ? `Edit: ${config.name}` : 'Add Partner API Config'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '75vh', overflowY: 'auto' }}>

          {/* Name + Role */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Config Name *</label>
              <input className="input-field" placeholder="e.g. your partner's name" value={form.name} onChange={e => update('name', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Role *</label>
              <select className="select-field" value={form.role} onChange={e => update('role', e.target.value)}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          {/* Endpoint + Method */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>API Endpoint URL *</label>
              <input className="input-field" placeholder="https://reporting.partner.com/api/v1/report" value={form.endpoint_url} onChange={e => update('endpoint_url', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Method</label>
              <select className="select-field" style={{ width: '90px' }} value={form.method} onChange={e => update('method', e.target.value)}>
                {METHODS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Auth */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Authentication Type</label>
              <select className="select-field" value={form.auth_type} onChange={e => update('auth_type', e.target.value)}>
                {AUTH_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            {form.auth_type !== 'none' && (() => {
              const meta = AUTH_FIELD_META[form.auth_type] || AUTH_FIELD_META.bearer;
              const hasSavedToken = config?.id && config.has_token;
              return (
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                    {meta.label} {hasSavedToken
                      ? <span style={{ color: 'var(--green)', fontWeight: 400 }}>(saved — leave blank to keep existing)</span>
                      : '*'}
                  </label>
                  <input className="input-field" type="password"
                    autoComplete="new-password"
                    name={`partner_cred_${form.auth_type}`}
                    placeholder={hasSavedToken ? '••••••••••••' : meta.placeholder}
                    value={form.auth_token} onChange={e => update('auth_token', e.target.value)} />
                  {meta.hint && (
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.4 }}>
                      {meta.hint}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Second credential — optional. Some partners require two headers at
              once (e.g. X-API-Key AND Authorization: Bearer). Hidden behind
              'None' so the common single-credential case stays uncluttered. */}
          {form.auth_type !== 'topon_md5' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                  Second Credential
                </label>
                <select className="select-field" value={form.auth_type_2 || 'none'}
                  onChange={e => update('auth_type_2', e.target.value)}>
                  {AUTH_TYPES_2.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              {form.auth_type_2 && form.auth_type_2 !== 'none' && (() => {
                const meta2 = AUTH_FIELD_META[form.auth_type_2] || AUTH_FIELD_META.bearer;
                const hasSaved2 = config?.id && config.has_token_2;
                const clash = AUTH_HEADER_OF[form.auth_type] &&
                              AUTH_HEADER_OF[form.auth_type] === AUTH_HEADER_OF[form.auth_type_2];
                return (
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                      {meta2.label} {hasSaved2
                        ? <span style={{ color: 'var(--green)', fontWeight: 400 }}>(saved — leave blank to keep existing)</span>
                        : '*'}
                    </label>
                    <input className="input-field" type="password"
                      autoComplete="new-password"
                      name={`partner_cred2_${form.auth_type_2}`}
                      placeholder={hasSaved2 ? '••••••••••••' : meta2.placeholder}
                      value={form.auth_token_2} onChange={e => update('auth_token_2', e.target.value)} />
                    {clash ? (
                      <div style={{ fontSize: '10px', color: 'var(--red)', marginTop: '3px', lineHeight: 1.4 }}>
                        Both credentials write the {AUTH_HEADER_OF[form.auth_type]} header — one would overwrite the other. Pick a different type.
                      </div>
                    ) : meta2.hint && (
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.4 }}>
                        {meta2.hint}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Headers */}
          <JsonField label="Request Headers (JSON)" value={form.headers} onChange={v => update('headers', v)}
            hint={'Custom headers merged with auth. Use {date_from} and {date_to} as placeholders. Example: {"X-Publisher-ID":"abc123"}'} />

          {/* Request Body */}
          <JsonField label={`Request Body (JSON) — Method: ${form.method}`} value={form.request_body} onChange={v => update('request_body', v)}
            hint={'Use {date_from} and {date_to} placeholders. For GET requests this becomes query params. Example: {"startDate":"{date_from}","endDate":"{date_to}","metrics":["impressions","revenue"]}'} />

          {/* Response Mapping */}
          <JsonField label="Response Field Mapping (JSON)" value={form.response_mapping} onChange={v => update('response_mapping', v)}
            hint={'Map API field names to standard fields. "rows_path" is the dot-path to the data array (e.g. "data.report"). Leave empty if the response is already a top-level array.'} />

          {/* API Docs URL */}
          <div>
            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>API Documentation URL (optional)</label>
            <input className="input-field" placeholder="https://docs.partner.com/reporting-api" value={form.docs_url} onChange={e => update('docs_url', e.target.value)} />
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.5 }}>Used in the Fetch Engine tab to show available dimensions and metrics for this partner.</div>
          </div>

          {/* Bundle dimension — powers Auto Monitor bundle-level discrepancy */}
          <div>
            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Bundle Dimension (optional)</label>
            <input className="input-field" placeholder="e.g. app_bundle, bundle_id, app_id" value={form.bundle_dimension || ''} onChange={e => update('bundle_dimension', e.target.value)} />
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.5 }}>The API request dimension that returns the app bundle ID. Set this to enable the Auto Monitor's "Fetch bundle-level discrepancy" per day. Leave empty if this partner doesn't report bundle-level data.</div>
          </div>

          {/* Date Format + Notes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Date Format</label>
              <input className="input-field" placeholder="YYYY-MM-DD" value={form.date_format} onChange={e => update('date_format', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Notes (optional)</label>
              <input className="input-field" placeholder="e.g. any context for your team" value={form.notes} onChange={e => update('notes', e.target.value)} />
            </div>
          </div>

          {/* ─── Sprint 3 + 3.5: Setup Reporting Capabilities ─────────────────
              Outer parent section holds two semantic sub-sections:
                1. Field Mappings    — WHAT fields this partner has (dims, metrics)
                2. Filter Setup      — HOW filters work for this partner (shape, operators)
              "What" comes before "how" — define the fields, then describe filtering. */}
          <div style={{ marginTop: '8px', paddingTop: '14px', borderTop: '1px dashed var(--border)' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
              Setup Reporting Capabilities
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '14px', lineHeight: 1.5 }}>
              Describe what this partner's API exposes — which fields it returns, and how filtering works.
              Click "🪄 Suggest from docs" on Dimensions or Metrics to auto-detect from the documentation URL above.
            </div>

            {/* ─── Sub-section 1: Field Mappings ────────────────────────── */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Field Mappings
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.5 }}>
                Which fields this partner returns — dimensions (groupable) and metrics (numeric).
              </div>

              {/* Dimensions Editor */}
              <div style={{ marginBottom: '12px' }}>
                <SyncedListEditor
                  label="Dimensions"
                  value={form.dimensions}
                  onChange={items => update('dimensions', items)}
                  suggestions={detectedDims}
                  isDetecting={isDetecting}
                  onSuggest={handleSuggestFields}
                  helpText="Fields used to group data (e.g. date, country, app_bundle). One auto-detect call populates both dimensions and metrics."
                  addDefaults={k => ({ key: k, label: k, filterable: true })}
                />
              </div>

              {/* Metrics Editor */}
              <div style={{ marginBottom: '0' }}>
                <SyncedListEditor
                  label="Metrics"
                  value={form.metrics}
                  onChange={items => update('metrics', items)}
                  suggestions={detectedMetrics}
                  isDetecting={isDetecting}
                  onSuggest={handleSuggestFields}
                  helpText="Numeric measurements (e.g. impressions, revenue, ecpm). Same auto-detect call as Dimensions."
                />
              </div>
            </div>

            {/* ─── Sub-section 2: Filter Setup ──────────────────────────── */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Filter Setup
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.5 }}>
                How filtering works for this partner — the JSON shape filters take, and the operators it accepts.
              </div>

              {/* Filter Shape Picker */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px', fontWeight: 600 }}>Filter Shape</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {FILTER_SHAPES.map(opt => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '4px', background: form.filter_shape === opt.value ? 'var(--bg-elev)' : 'transparent', border: '1px solid ' + (form.filter_shape === opt.value ? 'var(--border)' : 'transparent') }}>
                      <input type="radio" name="filter_shape"
                        checked={form.filter_shape === opt.value}
                        onChange={() => update('filter_shape', opt.value)}
                        style={{ marginTop: '2px', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{opt.label}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.4, marginTop: '2px' }}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Filter Operators Editor — Sprint 3.5 */}
              <div style={{ marginBottom: '0' }}>
                <SyncedListEditor
                  label="Filter Operators"
                  value={form.operator_vocabulary}
                  onChange={items => update('operator_vocabulary', items)}
                  keyField="wire"
                  addDefaults={defaultOperatorAddDefaults}
                  presetValues={OPERATOR_PRESETS}
                  hideSuggestButton={true}
                  freeformPlaceholder="e.g. eq, IN, LIKE — partner-specific wire value"
                  helpText="Comparison operators this partner accepts in filters (e.g. =, in, contains). Each entry stores both display and wire values; click a preset chip to add, or type a custom one below."
                  preNote={
                    form.filter_shape === 'query-string-only'
                      ? "This partner doesn't support field filtering — operators won't be used at runtime, but you can still record them here for reference."
                      : null
                  }
                />
              </div>
            </div>
          </div>

          {/* Test result */}
          {testResult && (
            <div className={`alert ${testResult.success ? 'alert-success' : 'alert-error'}`} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
              <div style={{ fontWeight: 700 }}>{testResult.success ? '✓ Connection successful' : '✗ Test failed'}</div>
              {testResult.success && <div style={{ fontSize: '12px' }}>{testResult.message}</div>}
              {testResult.success && testResult.preview?.length > 0 && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', background: 'var(--bg-elevated)', padding: '8px', borderRadius: '4px', width: '100%', overflowX: 'auto' }}>
                  {testResult.preview.map((r, i) => (
                    <div key={i}>{JSON.stringify(r)}</div>
                  ))}
                </div>
              )}
              {!testResult.success && <div style={{ fontSize: '12px' }}>{testResult.error}</div>}
              {testResult.rawSample && <details style={{ fontSize: '11px', width: '100%' }}><summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Raw response sample</summary><pre style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{testResult.rawSample}</pre></details>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={handleTest} disabled={testing} title="Test connection with the current form values (no save until you click Save).">
            {testing ? <><RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }}/> Testing...</> : '⚡ Test Connection'}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}
              disabled={saving || !isTestValid}
              title={saveBlockedReason || ''}>
              {saving ? 'Saving...' : config?.id ? 'Update Config' : 'Save Config'}
            </button>
          </div>
        </div>
        {/* Sprint 2.5 (b): inline hint explaining why Save might be disabled */}
        {!isTestValid && (
          <div style={{ padding: '0 20px 12px 20px', fontSize: '11px', color: 'var(--amber)', lineHeight: 1.4, textAlign: 'right' }}>
            ⚠ {saveBlockedReason}
          </div>
        )}
      </div>
    </div>
  );
}

function PartnerAPIConfigs() {
  const [configs, setConfigs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [modal, setModal] = React.useState(null); // null | 'new' | configObj

  React.useEffect(() => {
    axios.get('/api/partner-configs')
      .then(r => setConfigs(r.data))
      .catch(() => toast.error('Failed to load partner configs'))
      .finally(() => setLoading(false));
  }, []);

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/partner-configs/${id}`);
      setConfigs(c => c.filter(x => x.id !== id));
      toast.success('Config deleted');
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  }

  function handleSaved(config) {
    setConfigs(prev => {
      const idx = prev.findIndex(c => c.id === config.id);
      return idx >= 0 ? prev.map(c => c.id === config.id ? config : c) : [...prev, config];
    });
    setModal(null);
  }

  const ROLE_COLORS = { demand: 'var(--purple)', supply: 'var(--green)', adserver: 'var(--cyan)' };
  const ROLE_LABELS = { demand: 'Demand', supply: 'Supply', adserver: 'Ad Server' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Save API configurations for your SSPs, DSPs and Ad Server. Use them to pull data directly into the Discrepancy Monitor without manual uploads.
        </div>
        <button className="btn btn-primary btn-sm" style={{ flexShrink: 0, marginLeft: '16px' }} onClick={() => setModal('new')}>
          <Plus size={12}/> Add Config
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }}/></div>
      ) : configs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '28px 20px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', border: '1px dashed var(--border)' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '10px' }}>No partner API configs yet</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px', maxWidth: '380px', margin: '0 auto 14px', lineHeight: 1.6 }}>
            Add your first config to start pulling reporting data directly from your partners' APIs into the Discrepancy Monitor.
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setModal('new')}><Plus size={12}/> Add First Config</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {configs.map(cfg => (
            <div key={cfg.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: ROLE_COLORS[cfg.role] || 'var(--text-muted)', flexShrink: 0 }}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>{cfg.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '2px' }}>
                  <span style={{ color: ROLE_COLORS[cfg.role] }}>{ROLE_LABELS[cfg.role]}</span>
                  <span>{cfg.method}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px' }}>{cfg.endpoint_url}</span>
                  {cfg.has_token && <span style={{ color: 'var(--green)' }}>🔑 Token saved</span>}
                  {cfg.has_token_2 && <span style={{ color: 'var(--green)' }}>🔑 2nd credential saved</span>}
                </div>
                {cfg.notes && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', fontStyle: 'italic' }}>{cfg.notes}</div>}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setModal(cfg)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(cfg.id, cfg.name)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <PartnerConfigModal
          config={modal === 'new' ? null : modal}
          onSave={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user, company } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifEmails, setNotifEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [savingEmails, setSavingEmails] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);

  useEffect(() => { loadInfo(); }, []);

  async function loadInfo() {
    try {
      const { data } = await axios.get('/api/account/info');
      setInfo(data);
      setNotifEmails(data.notification_emails || []);
    } catch { toast.error('Failed to load account info'); }
    finally { setLoading(false); }
  }

  async function requestPasswordReset() {
    try {
      const { data } = await axios.post('/api/account/request-password-reset');
      setResetRequested(true);
      toast.success(data.message);
    } catch { toast.error('Failed to request password reset'); }
  }

  async function saveNotifEmails() {
    setSavingEmails(true);
    try {
      await axios.patch('/api/account/notification-emails', { emails: notifEmails });
      toast.success('Notification emails saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingEmails(false); }
  }

  function addEmail() {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error('Invalid email address'); return; }
    if (notifEmails.includes(e)) { toast.error('Already added'); return; }
    setNotifEmails(prev => [...prev, e]);
    setNewEmail('');
  }

  if (loading) return <div style={{ textAlign: 'center', padding: '60px' }}><span className="spinner" /></div>;

  const plan = info?.plan || company?.plan || 'custom';
  const tools = info?.tools || company?.tools || [];
  const isSuite = plan === 'suite';
  const allTools = Object.entries(TOOL_LABELS);
  const availableTools = isSuite ? allTools : allTools.filter(([path]) => path === '/ads-txt' || tools.includes(path));

  return (
    <div>
      <h1 className="page-title">Account Info</h1>
      <p className="page-subtitle">Manage your company account, notification preferences and subscription details.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Left column */}
        <div>
          {/* Company & User Info */}
          <Section icon={User} title="Company & User" color="var(--cyan)">
            {[
              { label: 'Company Name', value: info?.name || company?.name, icon: Shield },
              { label: 'Email Address', value: info?.email || user?.email, icon: Mail },
              { label: 'Account Status', value: info?.status || 'Active', icon: CheckCircle },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <Icon size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <div style={{ minWidth: '130px', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{value || '—'}</div>
              </div>
            ))}
            <div style={{ marginTop: '14px' }}>
              {resetRequested ? (
                <div style={{ fontSize: '12px', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={13} /> Reset link sent to your email
                </div>
              ) : (
                <button className="btn btn-secondary btn-sm" onClick={requestPasswordReset}>
                  <Lock size={12} /> Request Password Reset
                </button>
              )}
            </div>
          </Section>

          {/* Notification Emails */}
          <Section icon={Bell} title="Critical Discrepancy Notifications" color="var(--amber)">
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px', lineHeight: 1.6 }}>
              These email addresses will receive alerts when the Discrepancy Monitor detects a <strong style={{ color: 'var(--red)' }}>Critical</strong> discrepancy during Auto checks (8AM &amp; 8PM daily).
            </div>
            <div style={{ marginBottom: '10px' }}>
              {notifEmails.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: '10px' }}>No notification emails configured yet.</div>
              ) : (
                notifEmails.map(e => (
                  <div key={e} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: '6px' }}>
                    <Mail size={12} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)', flex: 1, fontFamily: 'var(--font-mono)' }}>{e}</span>
                    <button onClick={() => setNotifEmails(prev => prev.filter(x => x !== e))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: '2px' }}><X size={12} /></button>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input className="input-field" style={{ flex: 1 }} type="email" placeholder="add@email.com"
                value={newEmail} onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addEmail()} />
              <button className="btn btn-secondary btn-sm" onClick={addEmail}><Plus size={12} /> Add</button>
            </div>
            <button className="btn btn-primary btn-sm" onClick={saveNotifEmails} disabled={savingEmails}>
              {savingEmails ? 'Saving...' : <><CheckCircle size={12} /> Save Emails</>}
            </button>
          </Section>
        </div>

        {/* Right column */}
        <div>
          {/* Payment Calendar FIRST */}
          <Section icon={Calendar} title="Next Payment Due" color="var(--green)">
            <PaymentCalendar renewsAt={info?.renews_at} />
          </Section>

          {/* Plan & Tools */}
          <Section icon={isSuite ? Zap : Wrench} title="Subscription Plan" color="var(--purple)">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <div style={{ width: 40, height: 40, borderRadius: '10px', background: isSuite ? 'color-mix(in srgb, var(--purple) 15%, transparent)' : 'color-mix(in srgb, var(--cyan) 10%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isSuite ? <Zap size={20} style={{ color: 'var(--purple)' }} /> : <Wrench size={20} style={{ color: 'var(--cyan)' }} />}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '16px', color: 'var(--text-primary)' }}>
                  {isSuite ? 'Full Suite' : 'Custom Plan'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {isSuite ? 'Access to all 10 tools' : `${availableTools.length} tool${availableTools.length !== 1 ? 's' : ''} enabled`}
                </div>
              </div>
              {info?.price_monthly && (
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, fontSize: '18px', color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>${info.price_monthly}<span style={{ fontSize: '12px', fontWeight: 400 }}>/mo</span></div>
                </div>
              )}
            </div>

            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: 700 }}>Available Tools</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {availableTools.map(([path, { label, tier }]) => (
                <div key={path} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                  <CheckCircle size={12} style={{ color: 'var(--green)', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)', flex: 1 }}>{label}</span>
                  <span className={`badge ${tier === 'Free' ? 'badge-green' : 'badge-purple'}`} style={{ fontSize: '9px' }}>{tier}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Partner API Configurations */}
          <Section icon={Link2} title="Partner API Configurations" color="var(--purple)">
            <PartnerAPIConfigs />
          </Section>

        </div>
      </div>
    </div>
  );
}
